import React, { useRef, useEffect, useState, useImperativeHandle, forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { updateCurrentModelLocally } from '../store/modelSlice';
import { exportaLlencPDF } from '../exportaPDF';
import { LLENC, colorPerIndex, tracPerIndex } from '../tema';
import {
  MIDES, esAresta, potSerCanonada, potSerConnector,
  puntValvula, geometriaCanonada, geometriaConnector,
  camiNuvol, camiValvula, propietatsInicials,
} from '../estocsFluxos';
import { compila, estatInicial, avanca, avaluats, derivades } from '../motorEstocs';
import { comprovaUnitats, unitatDeTemps } from '../unitats';
import { esNomReservat, PARAULA_TEMPS } from '../expressions';
import { arrossegaEnEsborrar } from '../services/mapesEstocsFluxos';
import AjudaEstocs from './AjudaEstocs';

// EL LLENÇ D'ESTOCS I FLUXOS.
//
// Refet el 26/08/2026 (la notació i les fletxes) i ampliat el mateix dia amb la simulació.
//
// ── QUÈ S'HEREDA DEL LLENÇ CAUSAL I QUÈ NO ───────────────────────────────────────────────────
//
// La pregunta era si la dinàmica que es va fer per als bucles causals hi té sentit. La resposta
// és que **hi té MÉS sentit, no menys**, i el motiu val la pena escriure'l:
//
//   · **El node que s'omple i es buida.** Allà era una metàfora que es va triar perquè comparar
//     radis de cercles es fa malament. Aquí **un estoc ÉS un recipient**: omplir-lo i buidar-lo
//     és el dibuix literal del que la notació vol dir.
//   · **Les boletes per les fletxes.** Allà representaven «influència», que no és res que es
//     mogui. Aquí una canonada **mou matèria de debò**, i les boletes són el que passa per dins.
//     Per això **només van per les canonades i mai pels connectors**: un connector porta
//     informació, i la informació no viatja en paquets.
//   · **El gruix de la fletxa.** Allà, la influència entregada. Aquí, **el cabal**: és la
//     metàfora de la mateixa notació —una canonada més ampla passa més—.
//   · **La fletxa de tendència.** Igual: puja o baixa ara mateix.
//
// I dues coses que NO s'hereten, perquè aquí serien mentida:
//
//   · **El convertidor no s'omple.** No acumula res —n'hi ha prou amb el número—, i és
//     justament la distinció sobre la qual descansa tota la notació. Un núvol, encara menys:
//     és fora de la frontera i no té valor.
//   · **La marca de retard a la fletxa.** A la dinàmica de sistemes **un retard ÉS un estoc**.
//     Posar-hi una marca ensenyaria que és una propietat d'una fletxa, que és el contrari del
//     que s'ha d'aprendre.
//
// L'escala del nivell: un estoc no va de 0 a 100 com les variables causals, o sigui que el
// nivell és **relatiu al màxim que hagi assolit durant aquesta simulació**. El número de dins és
// el valor de debò; el nivell només diu on és respecte d'on ha arribat.

const MOSTREIG = 0.2;          // cada quant es guarda un punt, en temps de model
const MAX_PUNTS = 400;

const ModelCanvas = forwardRef(({ modelId, onSelectComponent, selectedComponent, onModeChange }, ref) => {
  const { t } = useTranslation();
  const svgRef = useRef(null);
  const dispatch = useDispatch();
  const { currentModel } = useSelector((state) => state.models);

  const [components, setComponents] = useState([]);
  const [mode, setMode] = useState('select');
  // TORNAR A TRIAR DESPRÉS D'AFEGIR.
  //
  // Deixar l'eina d'afegir posada després de posar una cosa és el que fa que **acabis omplint el
  // llenç sense voler**: poses un node, mires el que has fet, hi vas a tocar amb el ratolí, i n'hi
  // has posat un altre. La intuïció després de col·locar alguna cosa és **mirar-se-la**, no
  // col·locar-ne una altra.
  //
  // Val per a les peces i per a les fletxes acabades. **NO per a esborrar** —qui esborra sol
  // esborrar-ne unes quantes— ni per a moure el llenç ni per al llapis, que són continus per
  // naturalesa.
  //
  // I s'ha de dir A LA BARRA, que és a fora: fins avui el llenç causal ja hi tornava per dins i
  // el botó es quedava encès, de manera que la barra i el llenç deien coses diferents.
  const tornaATriar = () => { setMode('select'); if (onModeChange) onModeChange('select'); };
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [traçant, setTraçant] = useState(null);
  const [avis, setAvis] = useState(null);
  const [ajuda, setAjuda] = useState(false);
  const [editant, setEditant] = useState(null);         // { id, valor } mentre es rebateja
  const arrossegant = useRef(null);
  const panant = useRef(null);

  // ── LA SIMULACIÓ ───────────────────────────────────────────────────────────────────────────
  const [simulant, setSimulant] = useState(false);
  const [valors, setValors] = useState({});
  const [maxims, setMaxims] = useState({});
  const [tendencies, setTendencies] = useState({});
  const [serie, setSerie] = useState([]);
  const [tempsModel, setTempsModel] = useState(0);
  const [problemes, setProblemes] = useState([]);
  const [boletes, setBoletes] = useState([]);
  const [ajustos, setAjustos] = useState({ speed: 1, showValues: true, showParticles: true, showFlows: false });
  const [posGrafic, setPosGrafic] = useState(null);
  const [avisosUnitats, setAvisosUnitats] = useState([]);
  const [veureUnitats, setVeureUnitats] = useState(false);
  const [uTemps, setUTemps] = useState(null);
  const [veureGrafic, setVeureGrafic] = useState(true);
  const bucle = useRef(null);
  const viu = useRef({});      // el que el bucle d'animació necessita sense provocar re-render

  const cerca = (id) => components.find((c) => String(c.id) === String(id));

  useEffect(() => {
    if (currentModel?.type === 'stock_flow') setComponents(currentModel.components || []);
  }, [currentModel]);

  useEffect(() => {
    if (!avis) return undefined;
    const q = setTimeout(() => setAvis(null), 4500);
    return () => clearTimeout(q);
  }, [avis]);

  // LES UNITATS ES COMPROVEN SOLES, a cada canvi del dibuix, com els bucles del llenç causal.
  //
  // **Són avisos i no errors**: un model amb les unitats regirades corre igual de bé, i impedir
  // que corri seria impedir treballar a mig fer. El panell surt PLEGAT amb el recompte a la
  // capçalera, i només si hi ha alguna cosa a dir; un panell que sempre hi és s'aprèn a ignorar.
  // Tot el que depèn NOMÉS del dibuix es calcula aquí i es guarda, i no al cos del render.
  //
  // `unitatDeTemps` crida `comprovaUnitats` per dins, i al render es tornava a fer **a cada
  // fotograma de la simulació** —que és quan hi ha `setValors`, `setTendencies` i `setBoletes`—:
  // tornar a llegir totes les unitats i refer l'anàlisi dimensional seixanta vegades per segon
  // per a una cosa que només canvia quan es toca el dibuix.
  //
  // I aquí es mira també si les equacions compilen, **encara que no s'hagi premut Arrencar**:
  // esborrar un convertidor que una equació anomena deixa aquella equació apuntant al no-res, i
  // val més veure-ho quan passa que no pas d'aquí a mitja hora en voler simular. El model no
  // corre —això ja ho impedeix `arrenca`—, però la marca hi és des del primer moment.
  useEffect(() => {
    setAvisosUnitats(comprovaUnitats(components));
    setUTemps(unitatDeTemps(components));
    const c = compila(components);
    setProblemes(c.problemes || []);
  }, [components]);

  const desaAlModel = (llista) => {
    setComponents(llista);
    dispatch(updateCurrentModelLocally({ components: llista }));
  };

  // ── ARRENCAR, ATURAR, REINICIAR ────────────────────────────────────────────────────────────

  const atura = () => {
    if (bucle.current) cancelAnimationFrame(bucle.current);
    bucle.current = null;
    setSimulant(false);
  };

  const reinicia = () => {
    atura();
    setValors({}); setMaxims({}); setTendencies({}); setSerie([]);
    setTempsModel(0); setBoletes([]);
    // Els problemes NO s'esborren en reiniciar: des que es comproven a cada canvi del dibuix,
    // són una propietat del dibuix i no de la simulació. Esborrar-los aquí faria desaparèixer la
    // marca d'una equació trencada amb un clic que no té res a veure.
    setProblemes(compila(components).problemes || []);
  };

  const arrenca = (opcions = ajustos) => {
    const compilat = compila(components);
    if (compilat.problemes?.length) {
      setProblemes(compilat.problemes);
      // Un model que no compila no s'arrenca a mitges. Es diu QUÈ falla i de QUI és.
      setAvis(compilat.problemes.map((p) => {
        const qui = p.id ? `${cerca(p.id)?.name || ''}: ` : '';
        return qui + t(p.clau, { detall: p.detall || '' });
      }).join(' · '));
      return;
    }
    setProblemes([]);
    const inicial = estatInicial(components, compilat);
    viu.current = {
      compilat, estat: inicial, ajustos: opcions,
      maxims: {}, anterior: {}, ultimaMostra: -1, serie: [], interval: MOSTREIG, boletes: [],
    };
    setValors(avaluats(inicial.valors, compilat, inicial.t));
    setSimulant(true);

    let previ = performance.now();
    const passa = (ara) => {
      const v = viu.current;
      const segons = Math.min((ara - previ) / 1000, 0.1);   // un salt de pestanya no salta el model
      previ = ara;
      v.estat = avanca(v.estat, v.compilat, segons, v.ajustos.speed);
      const tots = avaluats(v.estat.valors, v.compilat, v.estat.t);

      // El màxim assolit, que és l'escala del nivell de cada estoc.
      for (const s of v.compilat.estocs) {
        v.maxims[s] = Math.max(v.maxims[s] ?? 0, Math.abs(tots[s] ?? 0), 1e-9);
      }
      // LA TENDÈNCIA SURT DE LA DERIVADA, no de comparar dos fotogrames.
      //
      // Comparar fotogrames sembla l'obvi i està malament aquí: a 60 Hz amb DT = 0,05 només un
      // fotograma de cada tres fa un pas, o sigui que **dos de cada tres donaven diferència zero**
      // i la fletxa parpellejava o no sortia mai. Ho va trobar la prova de fum de l'editor.
      //
      // I la derivada no és cap aproximació d'això: és exactament el que diu la notació —el net
      // del que entra menys el que surt— i el motor ja la calcula. Zero soroll i zero llindar
      // triat a ull.
      const { d: pendent } = derivades(v.estat.valors, v.compilat, v.estat.t);
      const tend = {};
      for (const s of v.compilat.estocs) {
        const escala = Math.max(v.maxims[s], 1e-9);
        tend[s] = Math.abs(pendent[s]) / escala > 1e-5 ? Math.sign(pendent[s]) : 0;
      }

      // La sèrie, amb el mateix aprimament que el llenç causal: quan arriba al topall es queda
      // un punt de cada dos i dobla l'interval. Així no perd mai el principi, que és el tros
      // interessant; tallar la cua feia desaparèixer l'arrencada sense avisar.
      if (v.estat.t - v.ultimaMostra >= v.interval) {
        v.ultimaMostra = v.estat.t;
        v.serie = [...v.serie, { t: Number(v.estat.t.toFixed(2)), ...tots }];
        if (v.serie.length > MAX_PUNTS) {
          v.serie = v.serie.filter((_, i) => i % 2 === 0);
          v.interval *= 2;
        }
        setSerie(v.serie);
      }

      if (v.ajustos.showParticles) v.boletes = mouBoletes(v.boletes, tots, v.compilat, segons * v.ajustos.speed);
      else v.boletes = [];

      setValors(tots); setMaxims({ ...v.maxims }); setTendencies(tend);
      setTempsModel(v.estat.t); setBoletes(v.boletes);
      bucle.current = requestAnimationFrame(passa);
    };
    bucle.current = requestAnimationFrame(passa);
  };

  useEffect(() => () => { if (bucle.current) cancelAnimationFrame(bucle.current); }, []);

  // LES BOLETES. Van per les canonades i **només per les canonades**: són la matèria que hi
  // passa. Un connector porta informació i la informació no viatja en paquets.
  //
  // El ritme al qual en surten és proporcional al cabal, o sigui que una canonada aturada no en
  // treu cap i una de plena en treu moltes. Això és el que fa que el dibuix digui alguna cosa:
  // amb un ritme fix serien decoració.
  const mouBoletes = (actuals, tots, compilat, dt) => {
    const escala = Math.max(...compilat.fluxos.map((f) => Math.abs(tots[f] ?? 0)), 1e-9);
    const mogudes = actuals
      .map((b) => ({ ...b, u: b.u + (Math.abs(tots[b.flux] ?? 0) / escala) * dt * 0.5 }))
      .filter((b) => b.u <= 1);
    for (const f of compilat.fluxos) {
      const cabal = Math.abs(tots[f] ?? 0) / escala;
      if (cabal > 0.02 && Math.random() < cabal * dt * 6 && mogudes.length < 120) {
        // El signe diu el sentit: un cabal negatiu va al revés del que diu la fletxa, i això
        // s'ha de veure en comptes d'amagar-ho amb un valor absolut.
        mogudes.push({ id: `${f}_${Math.random()}`, flux: f, u: 0, enrere: (tots[f] ?? 0) < 0 });
      }
    }
    return mogudes;
  };

  useImperativeHandle(ref, () => ({
    setMode: (m) => { setMode(m); setTraçant(null); setEditant(null); },
    obreAjuda: () => setAjuda(true),
    exportaPDF: (titol, peu) => exportaLlencPDF(svgRef.current, titol, peu),
    startSimulation: (o) => arrenca(o),
    stopSimulation: atura,
    resetSimulation: reinicia,
    updateSettings: (o) => { setAjustos(o); if (viu.current) viu.current.ajustos = o; },
    updateComponent: (canvis) => {
      // TIME i TEMPS no es poden fer servir de nom. Si es pogués, una equació que digués «TIME»
      // voldria dir dues coses alhora i el desempat el faria el tokenitzador en silenci — que és
      // exactament la mena de cosa que després ningú no sap reproduir.
      if (canvis.name !== undefined && esNomReservat(canvis.name)) {
        setAvis(t('sf.nomReservat', { paraules: Object.values(PARAULA_TEMPS).join(', ') }));
        return;
      }
      desaAlModel(components.map((c) => (String(c.id) === String(canvis.id)
        ? { ...c, ...canvis, properties: { ...(c.properties || {}), ...(canvis.properties || {}) } }
        : c)));
      onSelectComponent(canvis);
    },
  }));

  const puntDelLlenc = (e) => {
    const r = svgRef.current.getBoundingClientRect();
    return { x: (e.clientX - r.left - pan.x) / zoom, y: (e.clientY - r.top - pan.y) / zoom };
  };

  // ── AFEGIR ─────────────────────────────────────────────────────────────────────────────────

  const afegeixPeca = (tipus, x, y) => {
    const quants = components.filter((c) => c.type === tipus).length + 1;
    const nou = {
      id: `${tipus}_${Date.now()}_${quants}`,
      type: tipus,
      name: tipus === 'cloud' ? '' : `${t(`tools.${tipus}`)} ${quants}`,
      x, y,
      properties: propietatsInicials(tipus),
    };
    desaAlModel([...components, nou]);
    onSelectComponent(nou);
    tornaATriar();
  };

  const afegeixAresta = (tipus, origen, desti) => {
    const motiu = tipus === 'flow'
      ? potSerCanonada(origen, desti, components)
      : potSerConnector(origen, desti, components);
    if (motiu) { setAvis(t(motiu)); return; }
    const quants = components.filter((c) => c.type === tipus).length + 1;
    const nova = {
      id: `${tipus}_${Date.now()}_${quants}`,
      type: tipus,
      name: tipus === 'flow' ? `${t('tools.flow')} ${quants}` : '',
      x: (origen.x + desti.x) / 2,
      y: (origen.y + desti.y) / 2,
      properties: { ...propietatsInicials(tipus), from: origen.id, to: desti.id },
    };
    desaAlModel([...components, nova]);
    onSelectComponent(nova);
    tornaATriar();
  };

  // ── EL RATOLÍ ──────────────────────────────────────────────────────────────────────────────

  const clicaComponent = (e, c) => {
    e.stopPropagation();
    if (mode === 'delete') {
      desaAlModel(arrossegaEnEsborrar(components, c.id));
      onSelectComponent(null);
      tornaATriar();
      return;
    }
    if (mode === 'flow' || mode === 'connector') {
      if (!traçant) { setTraçant({ origen: c, x: c.x, y: c.y }); return; }
      afegeixAresta(mode, traçant.origen, c);
      setTraçant(null);
      return;
    }
    onSelectComponent(c);
  };

  // Doble clic per rebatejar al llenç mateix, com al llenç causal: posar nom és el gros del que
  // es feia obrint el panell, i aquí encara més perquè **el nom d'un component és el que se
  // n'escriu a les equacions**.
  const doblecClic = (e, c) => {
    e.stopPropagation();
    if (c.type === 'cloud') return;      // un núvol no en porta
    setEditant({ id: c.id, valor: c.name || '' });
  };

  const clicaLlenc = (e) => {
    if (editant) { setEditant(null); return; }
    if (traçant) { setTraçant(null); return; }
    const p = puntDelLlenc(e);
    if (mode === 'stock' || mode === 'converter' || mode === 'cloud') { afegeixPeca(mode, p.x, p.y); return; }
    onSelectComponent(null);
  };

  const baixaRatoli = (e) => {
    if (mode !== 'pan') return;
    panant.current = { x: e.clientX - pan.x, y: e.clientY - pan.y };
  };

  const comencaArrossegar = (e, c) => {
    if (mode !== 'select') return;
    e.stopPropagation();
    const p = puntDelLlenc(e);
    arrossegant.current = { id: c.id, dx: c.x - p.x, dy: c.y - p.y, valvula: c.type === 'flow' };
  };

  const mouRatoli = (e) => {
    if (panant.current) { setPan({ x: e.clientX - panant.current.x, y: e.clientY - panant.current.y }); return; }
    if (traçant) { const p = puntDelLlenc(e); setTraçant({ ...traçant, x: p.x, y: p.y }); return; }
    if (!arrossegant.current) return;
    const p = puntDelLlenc(e);
    const { id, dx, dy, valvula } = arrossegant.current;
    desaAlModel(components.map((c) => {
      if (String(c.id) !== String(id)) return c;
      if (valvula) {
        return { ...c, x: p.x + dx, y: p.y + dy,
                 properties: { ...(c.properties || {}), valvulaX: p.x + dx, valvulaY: p.y + dy } };
      }
      return { ...c, x: p.x + dx, y: p.y + dy };
    }));
  };

  const deixaRatoli = () => { arrossegant.current = null; panant.current = null; };

  // Zoom amb Ctrl + roda, cap al punter. Sense Ctrl la roda es deixa per a la pàgina.
  const roda = (e) => {
    if (!e.ctrlKey) return;
    e.preventDefault();
    const r = svgRef.current.getBoundingClientRect();
    const cx = e.clientX - r.left, cy = e.clientY - r.top;
    const nou = Math.max(0.4, Math.min(3, zoom * (e.deltaY < 0 ? 1.1 : 1 / 1.1)));
    setPan({ x: cx - (cx - pan.x) * (nou / zoom), y: cy - (cy - pan.y) * (nou / zoom) });
    setZoom(nou);
  };

  // ── DIBUIX ─────────────────────────────────────────────────────────────────────────────────

  const triat = (c) => String(selectedComponent?.id) === String(c.id);
  // Qui té l'equació trencada es marca al llenç. Dir «hi ha un error» sense dir on obliga a
  // repassar-ho tot a mà, i en un dibuix de vint components això és mitja classe.
  const trencat = (c) => problemes.some((p) => String(p.id) === String(c.id));
  const unitatDubtosa = (c) => avisosUnitats.some((a) => String(a.id) === String(c.id));
  const colorDe = (c, i) => c.properties?.color || colorPerIndex(i);
  const estocs = components.filter((c) => c.type === 'stock');
  const indexEstoc = (id) => estocs.findIndex((s) => String(s.id) === String(id));
  const nombre = (v) => (Math.abs(v) >= 1000 ? Math.round(v).toLocaleString()
    : Math.abs(v) >= 10 ? v.toFixed(1) : v.toFixed(2));

  const dibuixaPeca = (c) => {
    const sel = triat(c);
    const comu = {
      onClick: (e) => clicaComponent(e, c),
      onDoubleClick: (e) => doblecClic(e, c),
      onMouseDown: (e) => comencaArrossegar(e, c),
      className: mode === 'select' ? 'cursor-move' : 'cursor-pointer',
    };

    if (c.type === 'stock') {
      const { amplada, alcada } = MIDES.estoc;
      const val = valors[String(c.id)];
      const max = maxims[String(c.id)] || 1;
      // El nivell es retalla a [0, 1] PER DIBUIXAR, i el número de dins segueix sent el de debò.
      // Un estoc negatiu no s'amaga: es pinta buit i porta una marca.
      const nivell = simulant && val !== undefined ? Math.max(0, Math.min(1, val / max)) : null;
      const negatiu = simulant && val !== undefined && val < 0;
      const idx = indexEstoc(c.id);
      const color = colorDe(c, idx);
      const tend = tendencies[String(c.id)] || 0;
      return (
        <g key={c.id} {...comu}>
          <clipPath id={`niv-${c.id}`}>
            <rect x={c.x - amplada / 2} y={c.y - alcada / 2} width={amplada} height={alcada} rx="4" />
          </clipPath>
          <rect x={c.x - amplada / 2} y={c.y - alcada / 2} width={amplada} height={alcada} rx="4"
            fill={LLENC.estoc.farciment}
            stroke={negatiu || trencat(c) ? LLENC.enCurs : sel ? LLENC.estoc.seleccio : LLENC.estoc.vora}
            strokeWidth={sel || negatiu || trencat(c) ? 3 : 2} />
          {nivell !== null && nivell > 0 && (
            <rect x={c.x - amplada / 2} y={c.y + alcada / 2 - alcada * nivell}
              width={amplada} height={alcada * nivell}
              fill={color} opacity="0.35" clipPath={`url(#niv-${c.id})`} />
          )}
          <text x={c.x} y={simulant ? c.y - 2 : c.y + 5} textAnchor="middle" fontSize="13"
            fill={LLENC.text} stroke="#FFFFFF" strokeWidth="3" paintOrder="stroke"
            className="pointer-events-none select-none">{c.name}</text>
          {simulant && ajustos.showValues && val !== undefined && (
            <text x={c.x} y={c.y + 16} textAnchor="middle" fontSize="13" fontWeight="600"
              fill={negatiu ? LLENC.enCurs : LLENC.text} stroke="#FFFFFF" strokeWidth="3" paintOrder="stroke"
              className="pointer-events-none select-none">
              {nombre(val)}{tend !== 0 ? (tend > 0 ? ' ▲' : ' ▼') : ''}
            </text>
          )}
          {negatiu && (
            <text x={c.x + amplada / 2 - 8} y={c.y - alcada / 2 + 14} textAnchor="end" fontSize="11"
              fill={LLENC.enCurs} className="pointer-events-none select-none">⚠</text>
          )}
          {/* Un punt i no una vora: la vora ja vol dir «l'equació no compila», que és una altra
              cosa i més greu. Dues coses diferents no poden fer servir la mateixa marca. */}
          {unitatDubtosa(c) && (
            <circle cx={c.x - amplada / 2 + 8} cy={c.y - alcada / 2 + 8} r="3.5"
              fill={LLENC.enCurs} className="pointer-events-none" />
          )}
        </g>
      );
    }

    if (c.type === 'converter') {
      // Un convertidor NO s'omple: no acumula res. El número i prou.
      const val = valors[String(c.id)];
      return (
        <g key={c.id} {...comu}>
          <circle cx={c.x} cy={c.y} r={MIDES.convertidor.radi}
            fill={LLENC.convertidor.farciment}
            stroke={trencat(c) ? LLENC.enCurs : sel ? LLENC.convertidor.seleccio : LLENC.convertidor.vora}
            strokeWidth={sel || trencat(c) ? 3 : 2} />
          {simulant && ajustos.showValues && val !== undefined && (
            <text x={c.x} y={c.y + 4} textAnchor="middle" fontSize="11" fontWeight="600"
              fill={LLENC.text} className="pointer-events-none select-none">{nombre(val)}</text>
          )}
          {unitatDubtosa(c) && (
            <circle cx={c.x - MIDES.convertidor.radi * 0.72} cy={c.y - MIDES.convertidor.radi * 0.72}
              r="3.5" fill={LLENC.enCurs} className="pointer-events-none" />
          )}
          <text x={c.x} y={c.y + MIDES.convertidor.radi + 16} textAnchor="middle" fontSize="12"
            fill={LLENC.text} stroke="#FFFFFF" strokeWidth="3" paintOrder="stroke"
            className="pointer-events-none select-none">{c.name}</text>
        </g>
      );
    }

    return (
      <g key={c.id} {...comu}>
        <path d={camiNuvol(c.x, c.y)} fill={LLENC.nuvol.farciment}
          stroke={sel ? LLENC.nuvol.seleccio : LLENC.nuvol.vora} strokeWidth={sel ? 3 : 2} />
      </g>
    );
  };

  const cabalRelatiu = (id) => {
    if (!simulant) return null;
    const meus = components.filter((c) => c.type === 'flow').map((c) => Math.abs(valors[String(c.id)] ?? 0));
    const max = Math.max(...meus, 1e-9);
    return Math.abs(valors[String(id)] ?? 0) / max;
  };

  const dibuixaCanonada = (c) => {
    const origen = cerca(c.properties?.from), desti = cerca(c.properties?.to);
    if (!origen || !desti) return null;
    const v = puntValvula(c, origen, desti);
    const g = geometriaCanonada(origen, desti, v);
    const sel = triat(c);
    const color = sel ? LLENC.flux.seleccio : LLENC.flux.vora;
    // EL GRUIX ÉS EL CABAL: la metàfora de la mateixa notació, una canonada més ampla passa més.
    const cabal = cabalRelatiu(c.id);
    const gruix = cabal === null ? (sel ? 2.5 : 1.8) : 1.2 + cabal * 5;
    const val = valors[String(c.id)];
    return (
      <g key={c.id}>
        {g.linies.map((l, i) => <line key={i} {...l} stroke={color} strokeWidth={gruix} />)}
        <polygon points="0,0 -11,-5 -11,5" fill={color}
          transform={`translate(${g.punta.x},${g.punta.y}) rotate(${g.punta.angle})`} />
        <g transform={`translate(${v.x},${v.y}) rotate(${g.angle})`}
           onClick={(e) => clicaComponent(e, c)}
           onDoubleClick={(e) => doblecClic(e, c)}
           onMouseDown={(e) => comencaArrossegar(e, c)}
           className={mode === 'select' ? 'cursor-move' : 'cursor-pointer'}>
          <path d={camiValvula()} fill={LLENC.flux.farciment}
            stroke={trencat(c) ? LLENC.enCurs : color} strokeWidth={sel || trencat(c) ? 2.5 : 1.8} />
          <circle cx="0" cy="0" r={MIDES.valvula + 4} fill="transparent" />
        </g>
        {unitatDubtosa(c) && (
          <circle cx={v.x + MIDES.valvula} cy={v.y - MIDES.valvula} r="3.5"
            fill={LLENC.enCurs} className="pointer-events-none" />
        )}
        <text x={v.x} y={v.y - MIDES.valvula - 8} textAnchor="middle" fontSize="12" fill={LLENC.text}
          stroke="#FFFFFF" strokeWidth="3" paintOrder="stroke"
          className="pointer-events-none select-none">{c.name}</text>
        {simulant && ajustos.showValues && val !== undefined && (
          <text x={v.x} y={v.y + MIDES.valvula + 16} textAnchor="middle" fontSize="11" fontWeight="600"
            fill={LLENC.flux.seleccio} stroke="#FFFFFF" strokeWidth="3" paintOrder="stroke"
            className="pointer-events-none select-none">{nombre(val)}</text>
        )}
      </g>
    );
  };

  const dibuixaConnector = (c) => {
    const origen = cerca(c.properties?.from), desti = cerca(c.properties?.to);
    if (!origen || !desti) return null;
    const punt = (p) => (p.type === 'flow'
      ? { ...p, ...puntValvula(p, cerca(p.properties?.from), cerca(p.properties?.to)) }
      : p);
    const g = geometriaConnector(punt(origen), punt(desti));
    const sel = triat(c);
    const color = sel ? LLENC.estoc.seleccio : LLENC.linia;
    return (
      <g key={c.id} onClick={(e) => clicaComponent(e, c)} onDoubleClick={(e) => doblecClic(e, c)}
         className="cursor-pointer">
        <path d={g.d} fill="none" stroke={color} strokeWidth={sel ? 2.2 : 1.3} />
        <polygon points="0,0 -9,-4 -9,4" fill={color}
          transform={`translate(${g.p1.x},${g.p1.y}) rotate(${g.angle})`} />
        {c.name && (
          <text x={g.punt(0.5).x} y={g.punt(0.5).y - 6} textAnchor="middle" fontSize="11"
            fill={LLENC.textPal} stroke="#FFFFFF" strokeWidth="3" paintOrder="stroke"
            className="pointer-events-none select-none">{c.name}</text>
        )}
        <path d={g.d} fill="none" stroke="transparent" strokeWidth="14" />
      </g>
    );
  };

  // Les boletes: on és cadascuna ara, damunt de la canonada que li toca.
  const dibuixaBoletes = () => boletes.map((b) => {
    const f = cerca(b.flux);
    if (!f) return null;
    const origen = cerca(f.properties?.from), desti = cerca(f.properties?.to);
    if (!origen || !desti) return null;
    const v = puntValvula(f, origen, desti);
    const g = geometriaCanonada(origen, desti, v);
    const u = b.enrere ? 1 - b.u : b.u;
    const x = g.origenAncora.x + (g.destiAncora.x - g.origenAncora.x) * u;
    const y = g.origenAncora.y + (g.destiAncora.y - g.origenAncora.y) * u;
    return <circle key={b.id} cx={x} cy={y} r="3.2" fill={LLENC.flux.seleccio} opacity="0.85" />;
  });

  const peces = components.filter((c) => !esAresta(c.type));
  const canonades = components.filter((c) => c.type === 'flow');
  const connectors = components.filter((c) => c.type === 'connector');

  // EL GRÀFIC. **Els estocs sols per defecte**, i és una decisió: al llenç causal totes les
  // variables vivien entre 0 i 100 i un sol eix servia. Aquí un estoc en litres i un flux en
  // litres/minut no comparteixen ni unitat ni ordre de magnitud, i posar-los al mateix eix diria
  // coses que no són. Els fluxos s'hi poden afegir amb l'interruptor, sabent què s'hi fa.
  const seriesGrafic = [
    ...estocs.map((c, i) => ({ id: String(c.id), nom: c.name, color: colorDe(c, i), trac: tracPerIndex(i) })),
    ...(ajustos.showFlows ? canonades.map((c, i) => ({
      id: String(c.id), nom: c.name, color: colorDe(c, estocs.length + i),
      trac: tracPerIndex(estocs.length + i) })) : []),
  ];
  const unitats = [...new Set(estocs.map((c) => c.properties?.units).filter(Boolean))];
  // (`uTemps` es calcula a l'efecte de dalt: aquí és on `units` deixa de ser decoració, però
  //  recalcular-ho a cada fotograma seria pagar l'anàlisi dimensional seixanta cops per segon.)

  return (
    <div className="flex-1 relative overflow-hidden" style={{ background: LLENC.fons }}>
      <svg ref={svgRef} className={`w-full h-full ${mode === 'pan' ? 'cursor-grab' : ''}`}
        onClick={clicaLlenc} onMouseDown={baixaRatoli} onMouseMove={mouRatoli}
        onMouseUp={deixaRatoli} onMouseLeave={deixaRatoli} onWheel={roda}>
        <defs>
          <pattern id="reixa-estocs" width="20" height="20" patternUnits="userSpaceOnUse">
            <path d="M 20 0 L 0 0 0 20" fill="none" stroke={LLENC.reixa} strokeWidth="1" />
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill="url(#reixa-estocs)" />
        <g data-contingut transform={`translate(${pan.x}, ${pan.y}) scale(${zoom})`}>
          {canonades.map(dibuixaCanonada)}
          {simulant && ajustos.showParticles && dibuixaBoletes()}
          {connectors.map(dibuixaConnector)}
          {peces.map(dibuixaPeca)}
          {traçant && (
            <line x1={traçant.origen.x} y1={traçant.origen.y} x2={traçant.x} y2={traçant.y}
              stroke={LLENC.enCurs} strokeWidth="2" strokeDasharray="5 4" />
          )}
        </g>
      </svg>

      {/* Rebatejar al llenç mateix. El camp se situa damunt del component. */}
      {editant && (() => {
        const c = cerca(editant.id);
        if (!c) return null;
        const desa = () => {
          const nom = editant.valor.trim();
          if (esNomReservat(nom)) {
            setAvis(t('sf.nomReservat', { paraules: Object.values(PARAULA_TEMPS).join(', ') }));
            setEditant(null); return;
          }
          if (nom) desaAlModel(components.map((x) => (String(x.id) === String(c.id) ? { ...x, name: nom } : x)));
          setEditant(null);
        };
        return (
          <input autoFocus value={editant.valor}
            onChange={(e) => setEditant({ ...editant, valor: e.target.value })}
            onBlur={desa}
            onKeyDown={(e) => { if (e.key === 'Enter') desa(); if (e.key === 'Escape') setEditant(null); }}
            onClick={(e) => e.stopPropagation()}
            className="absolute z-30 px-2 py-1 text-sm text-center border border-primary-600 rounded-md
                       shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            style={{ left: c.x * zoom + pan.x - 70, top: c.y * zoom + pan.y - 14, width: 140 }} />
        );
      })()}

      {simulant && (
        <div className="absolute top-3 left-3 z-20 px-2.5 py-1 rounded-md bg-white border border-gray-200
                        shadow-sm text-[11px] text-gray-600 font-medium tabular-nums">
          {t('sim2.time')} {tempsModel.toFixed(1)}
        </div>
      )}

      {/* El gràfic. Es queda quan atures —el moment de mirar-lo és quan ha acabat— i només
          desapareix amb «Reiniciar». Es pot arrossegar per la capçalera. */}
      {(simulant || serie.length > 0) && veureGrafic && seriesGrafic.length > 0 && (
        <div className="absolute z-20 bg-white border border-gray-200 rounded-md shadow-lg"
          style={posGrafic
            ? { left: posGrafic.x, top: posGrafic.y, width: 420 }
            : { right: 12, bottom: 12, width: 420 }}>
          <div className="flex items-center justify-between px-3 py-1.5 border-b border-gray-200 cursor-move"
            onMouseDown={(e) => {
              const c0 = { x: e.clientX, y: e.clientY };
              const p0 = posGrafic || { x: e.currentTarget.parentElement.offsetLeft,
                                        y: e.currentTarget.parentElement.offsetTop };
              const mou = (ev) => setPosGrafic({
                x: Math.max(0, p0.x + ev.clientX - c0.x), y: Math.max(0, p0.y + ev.clientY - c0.y) });
              const deixa = () => { window.removeEventListener('mousemove', mou); window.removeEventListener('mouseup', deixa); };
              window.addEventListener('mousemove', mou); window.addEventListener('mouseup', deixa);
            }}>
            <span className="text-[11px] font-semibold text-gray-700">
              {t('sf.chartTitle')}{unitats.length === 1 ? ` (${unitats[0]})` : ''}
            </span>
            <button type="button" onClick={() => setVeureGrafic(false)}
              className="text-gray-500 hover:text-gray-700 text-base leading-none">&times;</button>
          </div>
          <div className="h-52 px-1 pb-1">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={serie} margin={{ top: 4, right: 8, left: 4, bottom: 16 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={LLENC.reixa} />
                <XAxis dataKey="t" tick={{ fontSize: 10 }} stroke={LLENC.textPal}
                  label={uTemps ? { value: uTemps, position: 'insideBottomRight', offset: -6,
                                    style: { fontSize: 10, fill: LLENC.textPal } } : undefined} />
                <YAxis tick={{ fontSize: 10 }} stroke={LLENC.textPal} width={46} />
                <Tooltip contentStyle={{ fontSize: 11 }} />
                <Legend verticalAlign="top" height={20} wrapperStyle={{ fontSize: 10 }} />
                {seriesGrafic.map((s) => (
                  <Line key={s.id} type="monotone" dataKey={s.id} name={s.nom} stroke={s.color}
                    strokeDasharray={s.trac} dot={false} strokeWidth={1.8} isAnimationActive={false} />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
      {(simulant || serie.length > 0) && !veureGrafic && (
        <button type="button" onClick={() => setVeureGrafic(true)}
          className="absolute right-3 bottom-3 z-20 px-2.5 py-1.5 rounded-md bg-white border
                     border-gray-300 text-[11px] text-gray-600 hover:bg-gray-50 shadow-sm">
          {t('sf.chartShow')}
        </button>
      )}

      {avis && (
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 max-w-xl px-4 py-2 rounded-md
                        bg-white border border-warn-400 shadow-sm text-sm text-gray-700 z-30">
          {avis}
        </div>
      )}

      {traçant && (
        <div className="absolute top-3 left-1/2 -translate-x-1/2 px-3 py-1.5 rounded-md
                        bg-white border border-gray-300 shadow-sm text-xs text-gray-600 z-20">
          {t(mode === 'flow' ? 'sf.traçantCanonada' : 'sf.traçantConnector')}
        </div>
      )}

      <div className="absolute bottom-4 left-4 flex flex-col gap-1.5">
        {[['+', () => setZoom(Math.min(zoom + 0.1, 3))],
          ['−', () => setZoom(Math.max(zoom - 0.1, 0.4))],
          ['⤢', () => { setZoom(1); setPan({ x: 0, y: 0 }); }]].map(([txt, fn]) => (
          <button key={txt} type="button" onClick={fn} title={t('sf.zoomCtrl')}
            className="w-8 h-8 bg-white border border-gray-300 rounded-md text-gray-600
                       hover:bg-gray-50 text-sm leading-none">{txt}</button>
        ))}
      </div>

      {/* EL PANELL D'UNITATS. Plegat, i només hi és si hi ha alguna cosa a dir. */}
      {avisosUnitats.length > 0 && (
        <div className="absolute z-20 bg-white border border-gray-200 rounded-md shadow-sm
                        max-w-sm text-xs"
             style={{ left: 12, top: simulant ? 44 : 12 }}>
          <button type="button" onClick={() => setVeureUnitats(!veureUnitats)}
            className="w-full flex items-center gap-2 px-2.5 py-1.5 hover:bg-gray-50 rounded-md">
            <span className="w-4 h-4 rounded-md bg-warn-100 text-warn-700 text-[10px]
                             font-bold flex items-center justify-center">{avisosUnitats.length}</span>
            <span className="font-medium text-gray-700">{t('sf.uTitol')}</span>
            <span className="ml-auto text-gray-500">{veureUnitats ? '▴' : '▾'}</span>
          </button>
          {veureUnitats && (
            <ul className="border-t border-gray-200 px-2.5 py-2 space-y-1.5 max-h-56 overflow-y-auto">
              {avisosUnitats.map((a, i) => (
                <li key={i} className="text-gray-700 leading-snug">
                  {a.id && (
                    <button type="button" onClick={() => onSelectComponent(cerca(a.id))}
                      className="font-medium text-primary-700 hover:underline mr-1">
                      {cerca(a.id)?.name || '?'}:
                    </button>
                  )}
                  {t(a.clau, { ...a, detall: a.detall || '' })}
                </li>
              ))}
              <li className="pt-1 text-gray-500 border-t border-gray-100">{t('sf.uNota')}</li>
            </ul>
          )}
        </div>
      )}

      {ajuda && <AjudaEstocs onTancar={() => setAjuda(false)} />}
    </div>
  );
});

export default ModelCanvas;
